import React, {useEffect, useState} from 'react'
import Switch from '../components/Switch'
import {createUseStyles} from 'react-jss'
import {Link, useNavigate, useParams} from 'react-router-dom';
import Card from '../components/Card'
import CardSection from '../components/CardSection'
import DetailsCard from '../components/DetailsCard'
import {Colors} from '../constants/colors'
import {formatDateString, formatDateTimeString} from '../utils/dateHelper'
import {ComplianceStatus, EmployerPaydayDataModel} from '../redux/types'
import type {CreateFreePayoutModel, PayoutSettings, UserPayoutCooldownPeriodModel} from '../redux/types'
import EditPayoutSettingsModal from '../components/EditPayoutSettingsModal';
import ConfirmModal from '../components/ConfirmModal'
import useUserActions from '../hooks/useUserActions';
import {useAppSelector} from '../redux/hooks';
import FullScreenLoadingIndicator from '../components/FullScreenLoadingIndicator';
import CreateFreePayoutModal from '../components/CreateFreePayoutModal';
import NavLink from '../components/NavLink';
import {faArrowLeft, faCheck, faExclamationTriangle, faPauseCircle, faTimes} from '@fortawesome/free-solid-svg-icons';
import DefaultLink from '../components/Link';
import {titleMarginBottom} from '../constants/layout';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import SetUserPhoneModal from '../components/SetUserPhoneModal';
import Button from '../components/Button';
import useCommunicationOptOutActions from '../hooks/useCommunicationOptOutActions';
import TextAreaModal from '../components/TextAreaModal';
import {formatMoney} from '../utils/moneyHelper';
import DetailValueRow from '../components/DetailValueRow';

const UserDetails = () => {
  const styles = useStyles()
  const {
    getUserDetails,
    setPayoutSettings,
    setLockState,
    setPhoneNumberVerificationState,
    setPhoneNumber,
    setInternalComment,
    createFreePayout
  } = useUserActions()

  const {setCommunicationOptOutStateForUser} = useCommunicationOptOutActions()

  const [payoutSettingsEditModalVisible, setPayoutSettingsEditModalVisible] = useState(false)
  const [confirmLockUserModalVisible, setConfirmLockUserModalVisible] = useState(false)
  const [createFreePayoutModalVisible, setCreateFreePayoutModalVisible] = useState(false)
  const [userPhoneModalVisible, setUserPhoneModalVisible] = useState(false)
  const [internalCommentModalVisible, setInternalCommentModalVisible] = useState(false)
  const navigate = useNavigate();

  const loading = useAppSelector(state => state.user.loading)
  const userDetails = useAppSelector(state => state.user.userDetails)
  const optOuts = useAppSelector(state => state.optOut.optOuts)

  const {id} = useParams<{ id: string }>();

  useEffect(() => {
    if (userDetails === null || userDetails.id !== id) {
      if (id) {
        getUserDetails(id)
      }
    }
  }, [])

  const isLocked = () => {
    return userDetails?.state === "locked"
  }

  const handlePayoutSettingsChange = (payoutSettings: PayoutSettings) => {
    if (id) {
      setPayoutSettings(id, payoutSettings)
    }
    setPayoutSettingsEditModalVisible(false)
  }

  const handleCreateFreePayout = (freePayout: CreateFreePayoutModel) => {
    if (id) {
      createFreePayout(id, freePayout);
    }
    setCreateFreePayoutModalVisible(false)
  }

  const handleLockStateChange = () => {
    if (!isLocked()) {
      setConfirmLockUserModalVisible(true)
    }
    else {
      setLockState(userDetails?.id as string, false)
    }
  }

  const handleLockUserConfirmed = () => {
    setLockState(userDetails?.id as string, true)
    setConfirmLockUserModalVisible(false)
  }

  const handlePhoneNumberVerificationStateChange = () => {
    if (userDetails) {
      setPhoneNumberVerificationState(userDetails.id as string, !userDetails.mobilePhoneVerificationDisabled)
    }
  }

  const handleUserPhoneUpdated = (phoneNumber: string) => {
    if (userDetails) {
      setPhoneNumber(userDetails.id, phoneNumber)
    }

    setUserPhoneModalVisible(false)
  }

  const handleInternalCommentChanged = (comment: string) => {
    if (userDetails) {
      setInternalComment(userDetails.id, comment)
    }

    setInternalCommentModalVisible(false)
  }

  const handleCommunicationOptOutStateChange = () => {
    if (userDetails) {
      var currentState = (optOuts?.userIds.find(x => x === userDetails?.id) !== undefined)
      setCommunicationOptOutStateForUser(userDetails.id as string, !currentState)
    }
  }

  const renderManuallyLockedSwitch = () => {
    return (
      <Switch
        id='lockSwitch'
        key='lockSwitch'
        checked={isLocked()}
        label={isLocked() ? 'Manual lock on' : 'Manual lock off'}
        onChange={() => handleLockStateChange()}/>
    )
  }

  const renderPhoneNumberVerificationSwitch = () => {
    return (
      <Switch
        id='phoneNumberVerification'
        key='phoneNumberVerification'
        checked={!userDetails?.mobilePhoneVerificationDisabled}
        label={!userDetails?.mobilePhoneVerificationDisabled ? 'Phone number verification on' : 'Phone number verification off'}
        onChange={() => handlePhoneNumberVerificationStateChange()}/>
    )
  }

  const getPayoutSettingSeverity = (value: unknown, highlightSetValues: boolean) => {
    return highlightSetValues && value != null ? 'warning' : undefined
  }

  const renderPayoutSettingsCardBody = (
    payoutSettings: PayoutSettings | undefined,
    noValueString: string,
    highlightSetValues = false
  ) => {
    const payoutSettingTooltip = 'User-specific payout setting is set'

    return (
      <div>
        <DetailValueRow
          label='Min amount per payout'
          value={payoutSettings?.minAmountPerPayout != null ? payoutSettings?.minAmountPerPayout : noValueString}
          severity={getPayoutSettingSeverity(payoutSettings?.minAmountPerPayout, highlightSetValues)}
          tooltip={payoutSettingTooltip}
        />
        <DetailValueRow
          label='Max amount per pay period'
          value={payoutSettings?.maxAmountPerPayday != null ? payoutSettings?.maxAmountPerPayday : noValueString}
          severity={getPayoutSettingSeverity(payoutSettings?.maxAmountPerPayday, highlightSetValues)}
          tooltip={payoutSettingTooltip}
        />
        <DetailValueRow
          label='Max percentage per pay period'
          value={payoutSettings?.maxPercentPerPayday != null ? payoutSettings?.maxPercentPerPayday : noValueString}
          severity={getPayoutSettingSeverity(payoutSettings?.maxPercentPerPayday, highlightSetValues)}
          tooltip={payoutSettingTooltip}
        />
        {/* <p>Max amount per payout: <span>{employer?.payoutSettings?.maxAmountPerPayout || notSpecified}</span></p> */}
        <DetailValueRow
          label='Max number of payouts per pay period'
          value={payoutSettings?.maxNumberOfPayoutsPerPayday != null ? payoutSettings?.maxNumberOfPayoutsPerPayday : noValueString}
          severity={getPayoutSettingSeverity(payoutSettings?.maxNumberOfPayoutsPerPayday, highlightSetValues)}
          tooltip={payoutSettingTooltip}
        />
        <DetailValueRow
          label='Fee'
          value={payoutSettings?.fee != null ? payoutSettings?.fee : noValueString}
          severity={getPayoutSettingSeverity(payoutSettings?.fee, highlightSetValues)}
          tooltip={payoutSettingTooltip}
        />
        <DetailValueRow
          label='Currency'
          value={payoutSettings?.currency?.toUpperCase() || noValueString}
          severity={getPayoutSettingSeverity(payoutSettings?.currency, highlightSetValues)}
          tooltip={payoutSettingTooltip}
        />
      </div>
    )
  }

  const getEmployerPaydayData = (employerId: string): EmployerPaydayDataModel | undefined => {
    return userDetails?.employerPaydayData?.find(x => x.employerId === employerId)
  }

  const renderPaydaysHeader = (employerId: string) => {
    const payoutSettings = getEmployerPaydayData(employerId)?.payoutSettings

    return (
      <Card className={styles.paydayContainer}>
        <div className={styles.payDayListCell}>
          <p>Payment date</p>
        </div>
        <div className={styles.payDayListCell}>
          <p>Total income gross/net ({payoutSettings?.currency?.toUpperCase()})</p>
        </div>
        <div className={styles.payDayListCell}>
          <p>Total payout/fee amount ({payoutSettings?.currency?.toUpperCase()})</p>
        </div>
        <div className={styles.payDayListCell}>
          <p>Num. payouts (done/left)</p>
        </div>
        <div className={styles.payDayListCell}>
          <p>Available ({payoutSettings?.currency?.toUpperCase()})</p>
        </div>
      </Card>
    )
  }

  const renderPaydays = (employerId: string) => {
    const paydays = getEmployerPaydayData(employerId)?.paydays

    if ((paydays?.length ?? 0) > 0) {
      const orderedPaydays = [...(paydays ?? [])].sort((a, b) => (new Date(a.payday.paymentDate) > new Date(b.payday.paymentDate)) ? -1 : 1)

      return orderedPaydays?.map((paydayData, index) => {
        const payoutAvailability = paydayData.payoutAvailability

        return (
          <Link to={{pathname: `/paydaydata/${employerId}/${paydayData.payday.id}`}} key={`payday_${index}`}>
            <Card
              className={`${styles.paydayContainer} ${styles.hover} ${!paydayData.payday.locked && styles.upcoming}`}>
              <div className={styles.payDayListCell}>
                <p><span>{formatDateString(paydayData.payday.paymentDate)}</span></p>
              </div>
              <div className={styles.payDayListCell}>
                <p>
                  <span>{formatMoney(paydayData.income.grossTotalAmount)} / {formatMoney(paydayData.income.netTotalAmount)}</span>
                </p>
              </div>
              <div className={styles.payDayListCell}>
                <p>
                  <span>{formatMoney(paydayData.payoutHistory.aggregatedAmount)} / {formatMoney(paydayData.payoutHistory.aggregatedFeeAmount)}</span>
                </p>
              </div>
              <div className={styles.payDayListCell}>
                <p>
                  <span>{paydayData.payoutHistory.payoutCount} / {payoutAvailability == null ? '-' : (payoutAvailability.availablePayoutCount ?? '∞')}</span>
                </p>
              </div>
              <div className={styles.payDayListCell}>
                <p>
                  <span>{formatMoney(payoutAvailability?.availableAmount)}</span>
                </p>
              </div>
            </Card>
          </Link>
        )
      })
    }
  }

  const renderEmployments = () => {
    return userDetails?.connectedEmployees?.map((employment, index) => {
      const dataForPaydays = getEmployerPaydayData(employment.employerId)
      const payoutSettings = dataForPaydays?.payoutSettings

      return (
        <div key={`employment_${index}`} className={styles.employmentContainer}>
          <h5>{employment.employerName}</h5>
          <CardSection>
            <DetailsCard>
              <h6>General information</h6>
              <p>Employment number: <span>{employment.externalId}</span></p>
              <p>State: <span>{employment.status}</span></p>
              <p>Connected at: <span>{formatDateTimeString(employment.connectedToUserDate)}</span></p>
              <p>Activation date: <span>{formatDateTimeString(employment.activationDate)}</span></p>
              <p>Employee: <DefaultLink to={`/employees/${employment.id}`}>Link</DefaultLink></p>
            </DetailsCard>
            <DetailsCard>
              <h6>Effective payout settings</h6>
              {renderPayoutSettingsCardBody(payoutSettings, 'not set')}
            </DetailsCard>
          </CardSection>
          <CardSection>
            <DetailsCard>
              <h6>Paydays</h6>
              {renderPaydaysHeader(employment.employerId)}
              {renderPaydays(employment.employerId)}
            </DetailsCard>
          </CardSection>
        </div>
      )
    })
  }

  const renderPayoutMethods = () => {
    return userDetails?.payoutMethods?.map((payoutMethod, index) => {
      return (
        <Card className={styles.card} key={`payoutMethod_${index}`}>
          <p>Type: <span>{payoutMethod.payoutMethodType}</span></p>
          <p>Target: <span>{payoutMethod.target}</span></p>
        </Card>
      )
    })
  }

  const renderPayoutCooldownPeriods = () => {
    if (!userDetails?.payoutCooldownPeriods || userDetails?.payoutCooldownPeriods.length === 0) {
      return null
    }

    const sortedCooldownPeriods = [...(userDetails.payoutCooldownPeriods as UserPayoutCooldownPeriodModel[])]
      .sort((a, b) => (~~b.isActive - ~~a.isActive) || Date.parse(b.end) - Date.parse(a.end))
      .slice(0, 10)

    return (
      <CardSection>
        <DetailsCard>
          <h6>{`Payout cooldown periods (showing ${sortedCooldownPeriods.length} of ${userDetails.payoutCooldownPeriods.length})`}</h6>
          <div className={styles.cooldownPeriodCardContainer}>
            {
              sortedCooldownPeriods.map((cooldownPeriod, index) => {
                const signingState = cooldownPeriod.state === 'active' ? 'Signed' : cooldownPeriod.state
                return (
                  <Card className={`${styles.card} ${styles.cooldownPeriodCard}`} key={`payoutCooldownPeriod_${index}`}>
                    <FontAwesomeIcon icon={faPauseCircle}
                                     color={cooldownPeriod.isActive ? Colors.yen_600 : Colors.darkGray3} size='lg'/>
                    <div style={{display: 'flex', flex: 1, flexDirection: 'column'}}>
                      <p>Start: <span>{formatDateTimeString(cooldownPeriod.start)}</span></p>
                      <p>End: <span>{formatDateTimeString(cooldownPeriod.end)}</span></p>
                      <p>Signing state: <span>{signingState}</span></p>
                      <DetailValueRow
                        label='Active'
                        value={cooldownPeriod.isActive ? 'yes' : 'no'}
                        severity={cooldownPeriod.isActive ? 'danger' : undefined}
                        tooltip='Active payout cooldown period restricts payouts'
                      />
                    </div>
                  </Card>
                )
              })
            }
          </div>
        </DetailsCard>
      </CardSection>
    )
  }

  const renderFreePayouts = () => {
    return (
      <CardSection>
        <DetailsCard iconType='Add' onClick={() => setCreateFreePayoutModalVisible(true)}>
          <h6>Free payouts</h6>
          <p>Available free payouts: <span>{userDetails?.freePayouts?.available}</span></p>
          <p>Used free payouts: <span>{userDetails?.freePayouts?.used}</span></p>
        </DetailsCard>
      </CardSection>
    )
  }

  const renderCommunicationOptOutSwitch = () => {
    return (
      <Switch
        id='communicationOptOutSwitch'
        key='communicationOptOutSwitch'
        checked={(optOuts?.userIds.find(x => x === userDetails?.id) !== undefined)}
        label='Communication opt-out'
        onChange={() => handleCommunicationOptOutStateChange()}/>
    )
  }

  const renderMonitoringLink = () => {
    if (userDetails?.complianceStatus === ComplianceStatus.unknown) {
      return <NavLink
        icon={faExclamationTriangle}
        iconColor={Colors.yellow}
        title='Missing compliance info'
        onClick={() => navigate(`/aml/users/${userDetails?.id}`)}/>
    }
    else if (userDetails?.complianceStatus === ComplianceStatus.compliant) {
      return <NavLink
        icon={faCheck}
        iconColor={Colors.green}
        title='Compliant'
        onClick={() => navigate(`/aml/users/${userDetails?.id}`)}/>
    }
    else if (userDetails?.complianceStatus === ComplianceStatus.nonCompliant) {
      return <NavLink
        icon={faTimes}
        iconColor={Colors.red}
        title='Non-Compliant'
        onClick={() => navigate(`/aml/users/${userDetails?.id}`)}/>
    }
  }

  const notSpecified = 'not specified'

  return (
    <div className={styles.container}>
      <h1>{userDetails?.givenName} {userDetails?.familyName}</h1>
      <div className={styles.actionBar}>
        <NavLink title='Back' icon={faArrowLeft} onClick={() => navigate(-1)}/>
        <div className={styles.actionBarRight}>
          {renderMonitoringLink()}
          <Button
            title='Set phone number'
            onClick={() => setUserPhoneModalVisible(true)}
          />
          <div style={{paddingBottom: 8}}>
            {renderPhoneNumberVerificationSwitch()}
          </div>
          <div style={{paddingBottom: 8}}>
            {renderManuallyLockedSwitch()}
          </div>
          <div style={{paddingBottom: 8}}>
            {renderCommunicationOptOutSwitch()}
          </div>
        </div>
      </div>
      <CardSection>
        <DetailsCard>
          <h6>General information</h6>
          <p>Name: <span>{userDetails?.givenName} {userDetails?.familyName}</span></p>
          <p>Identification number: <span>{userDetails?.identificationNumber || notSpecified}</span></p>
          <DetailValueRow
            label='State'
            value={userDetails?.state}
            severity={userDetails?.state && userDetails.state !== 'active' ? 'danger' : undefined}
            tooltip='User state is not active'
          />
          <DetailValueRow
            label='Compliance status'
            value={userDetails?.complianceStatus ?? notSpecified}
            severity={userDetails?.complianceStatus && userDetails.complianceStatus !== ComplianceStatus.compliant ? 'danger' : undefined}
            tooltip='User compliance status is not compliant'
          />
          <DetailValueRow
            label='App services'
            value={userDetails?.appServicesEnabled ? 'enabled' : 'disabled (Cappy Payout only)'}
          />
          <p>Created date: <span>{formatDateTimeString(userDetails?.createdDate)}</span></p>
          {userDetails?.accountDeletionRequestDate &&
              <p>Account deletion request date: <span>{formatDateString(userDetails?.accountDeletionRequestDate)}</span>
              </p>}
          {userDetails?.accountDeletionDate &&
              <p>Account deletion date: <span>{formatDateString(userDetails?.accountDeletionDate)}</span></p>}
          <DetailValueRow
            label='Data collection consent'
            value={userDetails?.dataCollectionConsent ? 'yes' : 'no'}
            severity={userDetails && !userDetails.dataCollectionConsent ? 'danger' : undefined}
            tooltip='User has not given data collection consent'
          />
          <DetailValueRow
            label='Activation guide completed'
            value={userDetails?.activationGuideCompleted ? 'yes' : 'no'}
            severity={userDetails && !userDetails.activationGuideCompleted ? 'warning' : undefined}
            tooltip='User has not completed the activation guide'
          />
          <p>Email: <span>{userDetails?.email || notSpecified}</span></p>
          <p>Phone number: <span>{userDetails?.mobilePhone || notSpecified}</span></p>
          {userDetails?.unverifiedMobilePhone &&
              <p>Unverified phone number: <span>{userDetails?.unverifiedMobilePhone}</span></p>}
          <DetailValueRow
            label='Phone number verification'
            value={userDetails?.mobilePhoneVerificationDisabled ? 'off' : 'on'}
            severity={userDetails?.mobilePhoneVerificationDisabled ? 'warning' : undefined}
            tooltip='Phone number verification is disabled'
          />
          <p>Market: <span>{userDetails?.marketName || notSpecified}</span></p>
          <p>Language: <span>{userDetails?.preferredLanguage || notSpecified}</span></p>
          <p>Last login: <span>{formatDateTimeString(userDetails?.latestLoginDate)}</span></p>
          <p>Number of logins: <span>{userDetails?.numberOfLogins}</span></p>
          <p>App version: <span>{userDetails?.appVersion}</span></p>
        </DetailsCard>
        <DetailsCard>
          <h6>Payout methods</h6>
          {renderPayoutMethods()}
        </DetailsCard>
        <DetailsCard onClick={() => setPayoutSettingsEditModalVisible(true)}>
          <h6>Payout settings</h6>
          {renderPayoutSettingsCardBody(userDetails?.payoutSettings, 'not set', true)}
        </DetailsCard>
      </CardSection>
      <CardSection>
        <DetailsCard iconType='Edit' onClick={() => setInternalCommentModalVisible(true)}>
          <h6>Internal comment</h6>
          <p><span>{userDetails?.internalComment}</span></p>
        </DetailsCard>
      </CardSection>
      {renderFreePayouts()}
      {renderPayoutCooldownPeriods()}
      <h2>Employments</h2>
      {renderEmployments()}
      <ConfirmModal
        visible={confirmLockUserModalVisible}
        confirmText={`Are you sure you want to lock this user? The user will not be able to use the app.`}
        onConfirmClick={handleLockUserConfirmed}
        onCancelClick={() => setConfirmLockUserModalVisible(false)}
      />
      <EditPayoutSettingsModal
        visible={payoutSettingsEditModalVisible}
        currencyEditable={false}
        initialValue={userDetails?.payoutSettings || {} as PayoutSettings}
        marketId={userDetails?.marketId ?? ''}
        onCancelClick={() => setPayoutSettingsEditModalVisible(false)}
        onSaveClick={handlePayoutSettingsChange}
      />
      <CreateFreePayoutModal
        visible={createFreePayoutModalVisible}
        onCancelClick={() => setCreateFreePayoutModalVisible(false)}
        onSaveClick={handleCreateFreePayout}
        maxCreateCount={10}
      />
      <SetUserPhoneModal
        visible={userPhoneModalVisible}
        initialValue={userDetails?.mobilePhone}
        onCancelClick={() => setUserPhoneModalVisible(false)}
        onSaveClick={handleUserPhoneUpdated}
      />
      <TextAreaModal
        title='Internal comment'
        visible={internalCommentModalVisible}
        initialValue={userDetails?.internalComment}
        onCancelClick={() => setInternalCommentModalVisible(false)}
        onSaveClick={handleInternalCommentChanged}
      />
      <FullScreenLoadingIndicator visible={loading}/>
    </div>
  )
}

export default UserDetails

const useStyles = createUseStyles({
  container: {
    '& h1': {
      marginBottom: titleMarginBottom
    },
    '& h2': {
      paddingTop: '30px'
    }
  },
  actionBar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: -8
  },
  actionBarRight: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: 15,
    justifyContent: 'flex-end',
    alignItems: 'center'
  },
  paydayContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    textAlign: 'left',
    padding: '3px 20px',
    margin: '10px 0',
    transition: '0.4s',
    border: '1px solid',
    borderColor: Colors.darkGray5
  },
  hover: {
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
    }
  },
  upcoming: {
    borderColor: Colors.euro_600
  },
  payDayListCell: {
    flex: 1,
    padding: '0px 10px'
  },
  employmentContainer: {
    paddingTop: 25,
    paddingBottom: 25
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'left',
    textAlign: 'left',
    padding: '5px 5px',
    margin: '10px 0',
    backgroundColor: Colors.darkGrayCard,
    transition: '0.4s',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
    }
  },
  cooldownPeriodCardContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10
  },
  cooldownPeriodCard: {
    width: 250,
    flexDirection: 'row',
    gap: 10,
    justifyContent: 'flex-start',
    border: '1px solid',
    borderColor: Colors.darkGray5
  },
  activeCooldownPeriod: {
    borderColor: Colors.yen_600
  }
})
